import _ from 'lodash';
import HTTPMethods from './HTTPMethods';
import { getIdToken, getImpersonatedUserId } from '../../primary/utilities/userTokenDetails';
import { getHmacCredentials } from '../../primary/utilities/hmac';
import AuthenticationType from './AuthenticationType';

class RequestDispatcher {
    /**
     * Dispatch request to API and gets response back to handle
     *
     * @param {string} url
     * @param {object} headers
     * @param {string} method
     * @param {boolean} authenticatedRoute
     * @param {string} authenticationType is the type of authentication required. Default will be token auth unless specified.
     * @param {object} body
     * @param {function} parseResponse
     */
    dispatchRequest = async (
        {
            url,
            headers = {},
            method = HTTPMethods.GET,
            authenticatedRoute = true,
            authenticationType = AuthenticationType.token,
            body = {},
            parseResponse = (response) => this.baseResponseParser(response),
        },
    ) => {
        const options = {
            method,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'x-api-key': this.getAPIKey(authenticatedRoute),
                ...headers,
            },
        };

        const authType = authenticationType?.toLowerCase();

        // automatically add authentication headers if route requires authentication
        if (authenticatedRoute && authType === AuthenticationType.token.toLowerCase()) {
            options.headers.Authorization = getIdToken();
        }

        if (authenticatedRoute && authType === AuthenticationType.hmac.toLowerCase()) {
            const credentials = getHmacCredentials(method, url);
            options.headers.Authorization = credentials.token;
            options.headers['x-fb-api-date'] = credentials.date;
        }

        const impersonatedCognitoId = getImpersonatedUserId();
        // if impersonated id is passed add impersonation header
        if (impersonatedCognitoId != null) {
            options.headers['x-impersonate-user'] = impersonatedCognitoId;
        }

        if (this.isBodySupported(method)) {
            options.body = this.getRequestBody(options.headers, body);
        }

        const response = await fetch(url, options);
        const data = {
            status: response.status,
            response: await response.json(),
        };

        // return this.baseResponseParser(data);
        return parseResponse(data);
    };

    /**
     * Request body format for POST and PUT requests
     *
     * @param headers
     * @param body
     * @returns {string|*}
     */
    getRequestBody = (headers, body) => {
        if (headers['Content-Type'].includes('x-www-form-urlencoded')) {
            return body;
        }

        return JSON.stringify(body, null, 2);
    };

    /**
     * Check to see how to handle API response that is sent back from request call
     *
     * @returns {*}
     */
    baseResponseParser = ({ status, response }) => {
        if (status === 401 || status === 403) {
            window.location.reload();
            return;
        }

        if (status > 199 && status < 300) {
            return response;
        }

        return this.rejectRequest(response);
    };

    /**
     * @param {object} error
     * @returns {Promise<never>}
     */
    rejectRequest = (error) => {
        // our default error messaging format from api's
        if (_.isObject(error) && error.userMessage) {
            return Promise.reject(error.userMessage);
        }

        return Promise.reject(error);
    };

    /**
     * Returns authenticated or un-authenticated api key for making API calls
     *
     * @param {boolean} authenticated
     * @returns {*}
     */
    getAPIKey = (authenticated) => (authenticated ? process.env.API_AUTHORISED_KEY : process.env.API_UNAUTHORISED_KEY);

    /**
     * Checks to see if body should be added to the request
     *
     * @param {string} method
     * @returns {boolean}
     */
    isBodySupported = (method) => {
        const supported = [
            HTTPMethods.POST,
            HTTPMethods.PUT,
            HTTPMethods.PATCH,
            HTTPMethods.DELETE,
        ];

        return supported.includes(method);
    };
}

export default RequestDispatcher;
